import React from "react";

const SSOSetupTutorial: React.FC = () => {
  return (
    <div className="min-h-dvh bg-neutral-100 dark:bg-neutral-900">
      <nav className="bg-rust dark:bg-blue-800 p-4">
        <div className="container mx-auto">
          <h1 className="text-3xl font-medium text-white">
            Setting Up SSO with Microsoft Entra ID / SAML
          </h1>
        </div>
      </nav>

      <div className="container mx-auto mt-8 px-4 pb-16">
        <div className="bg-n-00 dark:bg-neutral-800 rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-medium mb-4 dark:text-white">
            Introduction
          </h2>
          <p className="mb-4 text-neutral-700 dark:text-neutral-300">
            Single Sign-On (SSO) is a crucial feature for organizations looking
            to streamline their authentication processes and enhance security.
            This guide will walk you through the process of setting up SSO using
            Microsoft Entra ID (formerly known as Azure Active Directory) with a
            SAML connection.
          </p>

          <h2 className="text-2xl font-medium mt-8 mb-4 dark:text-white">
            Prerequisites
          </h2>
          <ul className="list-disc list-inside mb-4 text-neutral-700 dark:text-neutral-300">
            <li>
              An active Microsoft Entra ID account with administrative
              privileges
            </li>
            <li>Basic understanding of SAML protocol</li>
          </ul>

          <h2 className="text-2xl font-medium mt-8 mb-4 dark:text-white">
            Step-by-Step Guide
          </h2>

          <div className="space-y-6">
            <StepSection
              title="1. Create an Enterprise Application in Microsoft Entra ID"
              content={
                <>
                  <p className="mb-4">
                    The first step is to create an Enterprise Application in
                    Microsoft Entra ID. This will represent the application
                    you're integrating with SSO.
                  </p>
                  <ol className="list-decimal list-inside mb-4 ml-4">
                    <li>
                      Log in to the{" "}
                      <a
                        href="https://entra.microsoft.com/"
                        className="text-teal hover:underline dark:text-teal"
                      >
                        Microsoft Entra admin center
                      </a>
                    </li>
                    <li>
                      Navigate to "Identity" &gt; "Applications" &gt;
                      "Enterprise applications"
                    </li>
                    <li>Click on "+ New application"</li>
                    <li>
                      Choose "Create your own application" in order to set up a
                      new Enterprise application
                    </li>
                    <li>Follow the prompts to create the application</li>
                  </ol>
                </>
              }
            />

            <StepSection
              title="2. Configure SAML Single Sign-On"
              content={
                <>
                  <p className="mb-4">
                    Once you've created the Enterprise Application, you need to
                    configure SAML SSO for it.
                  </p>
                  <ol className="list-decimal list-inside mb-4 ml-4">
                    <li>
                      In the Enterprise Application overview, click on "Single
                      sign-on" in the left menu
                    </li>
                    <li>Select "SAML" as the single sign-on method</li>
                    <li>
                      You'll now need to configure the Basic SAML Configuration
                    </li>
                  </ol>
                </>
              }
            />

            <StepSection
              title="3. Set Up Basic SAML Configuration"
              content={
                <>
                  <p className="mb-4 font-medium">{`NOTE: Your <connection_identifier> will need to be obtained from the Zensei team prior to setup.`}</p>

                  <p className="mb-4">
                    Enter the following information in the Basic SAML
                    Configuration section:
                  </p>
                  <ul className="list-disc list-inside mb-4 ml-4">
                    <li>
                      Identifier (Entity ID):{" "}
                      <code className="bg-neutral-200 dark:bg-neutral-700 p-1 rounded">{`urn:auth0:zensei-prod:<connection_identifier>`}</code>
                    </li>
                    <li>
                      Reply URL (Assertion Consumer Service URL):{" "}
                      <code className="bg-neutral-200 dark:bg-neutral-700 p-1 rounded">{`https://zensei-prod.us.auth0.com/login/callback?connection=<connection_identifer>`}</code>
                    </li>
                    <li>
                      Sign on URL:{" "}
                      <code className="bg-neutral-200 dark:bg-neutral-700 p-1 rounded">{`https://app.zns.ai/auth/login`}</code>
                    </li>
                  </ul>
                </>
              }
            />

            {/* Add more StepSection components for the remaining steps */}
          </div>

          <h2 className="text-2xl font-medium mt-8 mb-4 dark:text-white">
            Testing Your SSO Configuration
          </h2>
          <p className="mb-4 text-neutral-700 dark:text-neutral-300">
            After completing all the steps, it's crucial to test your SSO
            configuration:
          </p>
          <ol className="list-decimal list-inside mb-4 ml-4 text-neutral-700 dark:text-neutral-300">
            <li>
              In the Microsoft Entra admin center, go to your Enterprise
              Application
            </li>
            <li>Click on "Single sign-on" in the left menu</li>
            <li>Scroll down to find the "Test single sign-on" section</li>
            <li>
              Click on "Test" and follow the prompts to test the SSO as a user
              would experience it
            </li>
          </ol>

          <h2 className="text-2xl font-medium mt-8 mb-4 dark:text-white">
            Conclusion
          </h2>
          <p className="mb-4 text-neutral-700 dark:text-neutral-300">
            Setting up SSO with Microsoft Entra ID and Zensei can significantly
            improve your organization's security posture and user experience. By
            following this guide, you should have successfully configured SSO
            for your users. Remember to keep your configurations up to date and
            regularly review access permissions to maintain a secure
            environment.
          </p>
        </div>
      </div>

      <footer className="bg-neutral-800 text-white p-4 mt-8">
        <div className="container mx-auto text-center">
          <p>&copy; 2024 Zensei. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const StepSection: React.FC<{ title: string; content: React.ReactNode }> = ({
  title,
  content,
}) => {
  return (
    <div className="border-b border-neutral-200 dark:border-neutral-700 pb-4">
      <h3 className="text-xl font-medium mb-2 dark:text-white">{title}</h3>
      <div className="text-neutral-700 dark:text-neutral-300">{content}</div>
    </div>
  );
};

export default SSOSetupTutorial;
